// Odoo API Service for Velaris Car Rental
// This service connects directly to the Odoo backend

const DEFAULT_ODOO_API_BASE = 'https://admin.velaris-royal.com/api/v1';
const ODOO_API_BASE = (
  process.env.REACT_APP_ODOO_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  DEFAULT_ODOO_API_BASE
).replace(/\/+$/, '');
const ODOO_MEDIA_ORIGIN = ODOO_API_BASE.replace(/\/api\/v1$/, '');
const DEFAULT_CONTACT_PHONE = process.env.REACT_APP_DEFAULT_PHONE || '';
const DEFAULT_CONTACT_PHONE_DISPLAY = process.env.REACT_APP_DEFAULT_PHONE_DISPLAY || DEFAULT_CONTACT_PHONE;
const ANALYTICS_SESSION_STORAGE_KEY = 'velaris_analytics_session';

function getRuntimeOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/`;
  }
  return 'http://localhost/';
}

function buildApiUrl(baseUrl, endpoint) {
  const normalizedBase = `${String(baseUrl || '').replace(/\/+$/, '')}/`;
  const absoluteBase = new URL(normalizedBase, getRuntimeOrigin()).toString();
  return new URL(String(endpoint || '').replace(/^\/+/, ''), absoluteBase);
}

// Default fallback images
const FALLBACK_IMAGES = {
  car: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&h=400&fit=crop',
  category: {
    luxury: 'https://images.unsplash.com/photo-1631295868223-63265b40d9e4?w=400&h=300&fit=crop',
    sports: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&h=300&fit=crop',
    suv: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=400&h=300&fit=crop',
    economy: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop',
    convertible: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=300&fit=crop',
    vans: 'https://images.unsplash.com/photo-1570733577524-3a047079e80d?w=400&h=300&fit=crop',
  },
  city: {
    dubai: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&h=600&fit=crop',
    'abu-dhabi': 'https://images.unsplash.com/photo-1512632578888-169bbbc64f33?w=1200&h=600&fit=crop',
    sharjah: 'https://images.unsplash.com/photo-1578895101408-1a36b834405b?w=1200&h=600&fit=crop',
    ajman: 'https://images.unsplash.com/photo-1466442929976-97f336a657be?w=1200&h=600&fit=crop',
  },
  brand: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=100&h=100&fit=crop',
};

// Brand logo URLs
const BRAND_LOGOS = {
  'rolls-royce': 'https://cdn.worldvectorlogo.com/logos/rolls-royce.svg',
  lamborghini: 'https://cdn.worldvectorlogo.com/logos/lamborghini.svg',
  ferrari: 'https://cdn.worldvectorlogo.com/logos/ferrari.svg',
  bentley: 'https://cdn.worldvectorlogo.com/logos/bentley-1.svg',
  mercedes: 'https://cdn.worldvectorlogo.com/logos/mercedes-benz-6.svg',
  bmw: 'https://cdn.worldvectorlogo.com/logos/bmw-2.svg',
  porsche: 'https://cdn.worldvectorlogo.com/logos/porsche-3.svg',
  audi: 'https://cdn.worldvectorlogo.com/logos/audi-11.svg',
};

// Brand short codes for logo display
const BRAND_CODES = {
  'rolls-royce': 'RR', 'rolls royce': 'RR',
  lamborghini: 'LB', ferrari: 'FR', bentley: 'BT',
  mercedes: 'MB', 'mercedes-benz': 'MB',
  bmw: 'BMW', porsche: 'PR', audi: 'AU',
  toyota: 'TY', nissan: 'NS', 'range rover': 'RR',
  mclaren: 'MC',
};

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function numberOrZero(value) {
  return Number(value ?? 0);
}

function isUsablePhoneNumber(phoneNumber) {
  const raw = String(phoneNumber || '').trim();
  if (!raw || /x/i.test(raw)) {
    return false;
  }

  return raw.replace(/\D/g, '').length >= 9;
}

function getDisplayPhoneNumber(phoneNumber, fallback = DEFAULT_CONTACT_PHONE_DISPLAY) {
  return isUsablePhoneNumber(phoneNumber) ? String(phoneNumber).trim() : fallback;
}

function sanitizePhoneNumber(phoneNumber, fallback = DEFAULT_CONTACT_PHONE) {
  const source = isUsablePhoneNumber(phoneNumber) ? phoneNumber : fallback;
  const normalized = String(source || fallback || '').replace(/[^0-9+]/g, '');
  return normalized || String(fallback || '').replace(/[^0-9+]/g, '');
}

function buildPhoneHref(phoneNumber, fallback = DEFAULT_CONTACT_PHONE) {
  const sanitized = sanitizePhoneNumber(phoneNumber, fallback);
  return sanitized ? `tel:${sanitized}` : '#';
}

function buildWhatsAppUrl(phoneNumber, message, fallback = DEFAULT_CONTACT_PHONE) {
  const sanitized = sanitizePhoneNumber(phoneNumber, fallback).replace(/[^0-9]/g, '');
  if (!sanitized) {
    return '#';
  }

  return `https://wa.me/${sanitized}?text=${encodeURIComponent(message || '')}`;
}

function normalizeMediaUrl(value) {
  if (!value) {
    return '';
  }

  const trimmed = String(value).trim();

  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  if (trimmed.startsWith('/')) {
    return `${ODOO_MEDIA_ORIGIN}${trimmed}`;
  }

  return `${ODOO_MEDIA_ORIGIN}/${trimmed.replace(/^\/+/, '')}`;
}

function normalizeMediaFields(payload, fields) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const normalized = { ...payload };
  fields.forEach((field) => {
    if (field in normalized) {
      normalized[field] = normalizeMediaUrl(normalized[field]);
    }
  });
  return normalized;
}

function normalizeApiPayload(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload ?? {};
  }

  if (payload.jsonrpc === '2.0') {
    if (payload.error) {
      return {
        success: false,
        error: payload.error.message || 'Request failed',
      };
    }
    return payload.result ?? {};
  }

  if (payload.error && typeof payload.error === 'object') {
    return {
      ...payload,
      error: payload.error.message || 'Request failed',
    };
  }

  return payload;
}

function generateAnalyticsSessionId(prefix = 'web') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Map Odoo car response to component-friendly format
function mapCar(car) {
  const basePricePerDay = numberOrZero(car.base_price_per_day ?? car.price_per_day);
  const basePricePerWeek = numberOrZero(car.base_price_per_week ?? car.price_per_week);
  const basePricePerMonth = numberOrZero(car.base_price_per_month ?? car.price_per_month);
  const effectivePricePerDay = numberOrZero(car.effective_price_per_day ?? car.price_per_day);
  const effectivePricePerWeek = numberOrZero(car.effective_price_per_week ?? car.price_per_week);
  const effectivePricePerMonth = numberOrZero(car.effective_price_per_month ?? car.price_per_month);
  const savingsDay = numberOrZero(car.day_savings);
  const savingsWeek = numberOrZero(car.week_savings);
  const savingsMonth = numberOrZero(car.month_savings);
  const discountPercent = numberOrZero(
    car.discount_percent ?? car.active_offer?.discount_percent,
  );
  return {
    id: car.id,
    name: car.name || `${car.brand} ${car.model}`,
    brand: car.brand || '',
    brandSlug: car.brand_slug || '',
    model: car.model || '',
    year: car.year || 0,
    pricePerDay: effectivePricePerDay,
    pricePerWeek: effectivePricePerWeek,
    pricePerMonth: effectivePricePerMonth,
    basePricePerDay,
    basePricePerWeek,
    basePricePerMonth,
    effectivePricePerDay,
    effectivePricePerWeek,
    effectivePricePerMonth,
    currency: car.currency || 'AED',
    category: car.category || '',
    categorySlug: car.category_slug || '',
    seats: car.seats || 0,
    doors: car.doors || 0,
    transmission: capitalize(car.transmission || ''),
    fuelType: capitalize(car.fuel_type || ''),
    color: car.color || car.color_slug || car.exterior_color || '',
    colorSlug: car.color_slug || car.color || car.exterior_color || '',
    exteriorColor: car.exterior_color || car.color || car.color_slug || '',
    exteriorColorLabel: car.exterior_color_label || '',
    interiorColor: car.interior_color || '',
    engine: car.engine || '',
    power: car.power || '',
    city: car.city || '',
    citySlug: car.city_slug || '',
    supplier: car.supplier || '',
    supplierId: car.supplier_id || null,
    supplierPhone: car.supplier_phone || '',
    supplierWhatsApp: car.supplier_whatsapp || car.supplier_phone || '',
    supplierLogo: normalizeMediaUrl(car.supplier_logo),
    image: normalizeMediaUrl(car.image),
    description: car.description || '',
    isFeatured: car.is_featured || false,
    state: car.state || '',
    slug: car.slug || '',
    features: car.features || [],
    activeOffer: car.active_offer || null,
    discount_percent: discountPercent,
    savingsDay,
    savingsWeek,
    savingsMonth,
    originalPrice: basePricePerDay > effectivePricePerDay ? basePricePerDay : null,
    gallery: (car.gallery || [])
      .map((galleryItem) => normalizeMediaUrl(galleryItem?.url || galleryItem))
      .filter(Boolean),
    securityDeposit: car.security_deposit || 0,
  };
}

function mapCategory(cat) {
  return {
    id: cat.id,
    name: cat.name || '',
    slug: cat.slug || '',
    description: cat.description || '',
    image: normalizeMediaUrl(cat.image) || '',
    carCount: cat.car_count || 0,
  };
}

function mapCity(city) {
  return {
    id: city.id,
    name: city.name || '',
    slug: city.slug || '',
    description: city.description || '',
    image: normalizeMediaUrl(city.image) || FALLBACK_IMAGES.city[city.slug] || FALLBACK_IMAGES.car,
    carCount: city.car_count || 0,
    supplierCount: city.supplier_count || 0,
    country: city.country || 'UAE',
  };
}

function mapBrand(brand) {
  return {
    id: brand.id,
    name: brand.name || '',
    slug: brand.slug || '',
    logo: normalizeMediaUrl(brand.logo) || BRAND_LOGOS[brand.slug] || '',
    logoCode: BRAND_CODES[brand.slug] || BRAND_CODES[brand.name?.toLowerCase()] || brand.name?.substring(0, 2)?.toUpperCase() || '',
    description: brand.description || '',
    isPremium: brand.is_premium || false,
    carCount: brand.car_count || 0,
  };
}

function compareByNameAsc(left, right) {
  return (left?.name || '').localeCompare(right?.name || '', undefined, {
    sensitivity: 'base',
  });
}

function mapSupplier(sup) {
  return {
    id: sup.id,
    name: sup.name || '',
    slug: sup.slug || '',
    email: sup.email || '',
    phone: sup.phone || '',
    whatsapp: sup.whatsapp || sup.phone || '',
    website: sup.website || '',
    city: sup.city || '',
    logo: normalizeMediaUrl(sup.logo),
    description: sup.description || '',
    isVerified: sup.is_verified || false,
    isPremium: sup.is_premium || false,
    carCount: sup.car_count || 0,
    rating: sup.rating || 0,
    reviewCount: sup.review_count || 0,
  };
}

function mapPartnerLogo(partner) {
  return {
    id: partner.id,
    name: partner.name || '',
    logo: normalizeMediaUrl(partner.logo_url),
    website: partner.website_url || '',
    category: partner.category || 'partner',
  };
}

function mapSupplierAsPartner(supplier) {
  const mappedSupplier = mapSupplier(supplier);
  return {
    id: `supplier-${mappedSupplier.id}`,
    name: mappedSupplier.name,
    logo: mappedSupplier.logo,
    website: mappedSupplier.website,
    category: 'partner',
  };
}

function mapOffer(offer) {
  return {
    id: offer.id,
    title: offer.title || '',
    enabled: offer.enabled !== false,
    startAt: offer.start_at || null,
    endAt: offer.end_at || null,
    periods: offer.periods || {},
    partners: (offer.partners || []).map(mapSupplier),
    cars: (offer.cars || []).map(mapCar),
    carCount: offer.car_count || 0,
  };
}

function normalizeSuggestionParams(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return {};
  }

  return Object.entries(params).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[String(key)] = String(value);
    }
    return result;
  }, {});
}

function mapSearchSuggestion(suggestion) {
  const params = normalizeSuggestionParams(suggestion.params);
  return {
    id: suggestion.id || `${suggestion.section || 'suggestion'}-${suggestion.sequence || suggestion.text || suggestion.label}`,
    section: suggestion.section || '',
    text: suggestion.text || suggestion.label || '',
    label: suggestion.label || suggestion.text || '',
    type: suggestion.type || suggestion.typeLabel || '',
    typeLabel: suggestion.typeLabel || suggestion.type || '',
    targetType: suggestion.targetType || suggestion.target_type || 'query',
    targetValue: suggestion.targetValue || suggestion.target_value || '',
    params,
    url: suggestion.url || '',
    sequence: suggestion.sequence || 0,
  };
}

class OdooApiService {
  constructor() {
    this.baseUrl = ODOO_API_BASE;
    this.currentLang = localStorage.getItem('velaris_language') || 'en';
    this.currentCurrency = localStorage.getItem('velaris_currency') || 'AED';
    this.authToken = localStorage.getItem('velaris_token') || null;
    this.analyticsSessionId = null;
  }

  setLanguage(lang) {
    this.currentLang = lang;
  }

  setCurrency(currencyCode) {
    const normalized = String(currencyCode || '').trim().toUpperCase() || 'AED';
    this.currentCurrency = normalized;
  }

  setAuthToken(token) {
    this.authToken = token;
    if (token) {
      localStorage.setItem('velaris_token', token);
    } else {
      localStorage.removeItem('velaris_token');
    }
  }

  getAuthToken() {
    return this.authToken || localStorage.getItem('velaris_token');
  }

  getAnalyticsSessionId() {
    if (this.analyticsSessionId) {
      return this.analyticsSessionId;
    }

    if (typeof window === 'undefined') {
      return '';
    }

    try {
      const storedSessionId = window.sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);
      if (storedSessionId) {
        this.analyticsSessionId = storedSessionId;
        return storedSessionId;
      }

      const sessionId = generateAnalyticsSessionId();
      window.sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, sessionId);
      this.analyticsSessionId = sessionId;
      return sessionId;
    } catch {
      this.analyticsSessionId = generateAnalyticsSessionId();
      return this.analyticsSessionId;
    }
  }

  getCurrentPageUrl() {
    if (typeof window === 'undefined') {
      return '';
    }

    return `${window.location.pathname}${window.location.search}`;
  }

  async parseResponse(response) {
    const rawBody = await response.text();
    let parsedBody = {};

    if (rawBody) {
      try {
        parsedBody = JSON.parse(rawBody);
      } catch {
        parsedBody = { success: false, error: rawBody };
      }
    }

    const normalizedBody = normalizeApiPayload(parsedBody);

    if (!response.ok) {
      const message =
        normalizedBody?.error ||
        normalizedBody?.message ||
        `API Error: ${response.status}`;
      throw new Error(message);
    }

    return normalizedBody;
  }

  async request(endpoint, options = {}) {
    const url = buildApiUrl(this.baseUrl, endpoint);
    url.searchParams.append('lang', this.currentLang);
    url.searchParams.append('currency', this.currentCurrency || 'AED');
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, value);
        }
      });
    }

    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      method: options.method || 'GET',
      headers,
    };
    if (options.body) config.body = JSON.stringify(options.body);

    const response = await fetch(url.toString(), config);
    return await this.parseResponse(response);
  }

  // JSON-RPC style request for POST endpoints
  async jsonRpcRequest(endpoint, data = {}) {
    return await this.request(endpoint, {
      method: 'POST',
      body: data,
    });
  }

  // ==================== LANGUAGES ====================
  async getLanguages() {
    try {
      const data = await this.request('/languages');
      return data.languages || [];
    } catch {
      return [
        { code: 'en', name: 'English', native_name: 'English', direction: 'ltr', flag: 'gb' },
        { code: 'ar', name: 'العربية', native_name: 'العربية', direction: 'rtl', flag: 'ae' },
        { code: 'fr', name: 'French', native_name: 'Français', direction: 'ltr', flag: 'fr' },
        { code: 'de', name: 'German', native_name: 'Deutsch', direction: 'ltr', flag: 'de' },
        { code: 'ru', name: 'Russian', native_name: 'Русский', direction: 'ltr', flag: 'ru' },
      ];
    }
  }

  async getTranslations(langCode) {
    try {
      return await this.request(`/translations/${langCode}`);
    } catch {
      return {};
    }
  }

  async getCurrencies() {
    try {
      return await this.request('/currencies');
    } catch {
      return {
        baseCurrency: 'AED',
        currencies: [
          { code: 'AED', name: 'UAE Dirham', symbol: 'AED', countryCode: 'AE', rateFromBase: 1 },
          { code: 'USD', name: 'US Dollar', symbol: '$', countryCode: 'US', rateFromBase: 0.272 },
          { code: 'EUR', name: 'Euro', symbol: '€', countryCode: 'EU', rateFromBase: 0.251 },
        ],
      };
    }
  }

  // ==================== CARS ====================
  async getCars(filters = {}) {
    const data = await this.request('/cars', { params: filters });
    return {
      cars: (data.cars || []).map(mapCar),
      total: data.total || 0,
    };
  }

  async getCar(carId) {
    const data = await this.request(`/cars/${carId}`);
    return mapCar(data);
  }

  async getFeaturedCars() {
    const data = await this.request('/cars', { params: { featured: true, limit: 8 } });
    return (data.cars || []).map(mapCar);
  }

  async getOffers() {
    const data = await this.request('/offers');
    return (data.offers || []).map(mapOffer);
  }

  async getSearchSuggestions() {
    try {
      const data = await this.request('/search/suggestions');
      return {
        popular: (data.popular || []).map(mapSearchSuggestion),
        recommended: (data.recommended || []).map(mapSearchSuggestion),
      };
    } catch {
      return {
        popular: [],
        recommended: [],
      };
    }
  }

  // ==================== BRANDS ====================
  async getBrands() {
    const data = await this.request('/brands');
    return (data.brands || []).map(mapBrand).sort(compareByNameAsc);
  }

  async getBrand(slug) {
    const data = await this.request(`/brands/${slug}`);
    return mapBrand(data);
  }

  async getBrandCars(brandSlug) {
    const data = await this.request('/cars', { params: { brand: brandSlug, limit: 20 } });
    return (data.cars || []).map(mapCar);
  }

  // ==================== CATEGORIES ====================
  async getCategories() {
    const data = await this.request('/categories');
    return (data.categories || []).map(mapCategory);
  }

  async getCategory(slug) {
    const data = await this.request(`/categories/${slug}`);
    return mapCategory(data);
  }

  async getCategoryCars(categorySlug) {
    const data = await this.request('/cars', { params: { category: categorySlug, limit: 20 } });
    return (data.cars || []).map(mapCar);
  }

  // ==================== CITIES ====================
  async getCities() {
    const data = await this.request('/cities');
    return (data.cities || []).map(mapCity);
  }

  async getCity(slug) {
    const data = await this.request(`/cities/${slug}`);
    return mapCity(data);
  }

  async getCityCars(citySlug) {
    const data = await this.request('/cars', { params: { city: citySlug, limit: 20 } });
    return (data.cars || []).map(mapCar);
  }

  // ==================== SUPPLIERS ====================
  async getSuppliers(filters = {}) {
    const data = await this.request('/suppliers', { params: filters });
    return (data.suppliers || []).map(mapSupplier);
  }

  async getSupplier(supplierId) {
    const data = await this.request(`/suppliers/${supplierId}`);
    return mapSupplier(data);
  }

  async getSupplierCars(supplierId) {
    // The Odoo API filters cars by supplier - we use the supplier_id
    const data = await this.request('/cars', { params: { limit: 20 } });
    return (data.cars || []).filter(c => c.supplier_id === supplierId).map(mapCar);
  }

  // ==================== LEADS ====================
  async createLead(leadData) {
    const response = await this.request('/leads', { method: 'POST', body: leadData });

    if (response?.success !== false) {
      this.trackInquiry({ carId: leadData.car_id || leadData.carId });
    }

    return response;
  }

  async createContactMessage(messageData) {
    const response = await this.request('/contact-messages', { method: 'POST', body: messageData });

    if (response?.success !== false) {
      this.trackInquiry();
    }

    return response;
  }

  // ==================== CLICK TRACKING ====================
  async trackClick(clickData) {
    try {
      await this.request('/track/click', { method: 'POST', body: clickData });
    } catch {
      // Silently fail
    }
  }

  // ==================== CMS ====================
  async getCmsConfig() {
    try {
      const data = await this.request('/cms/config');
      return normalizeMediaFields(data, ['logo_url', 'favicon_url']);
    } catch {
      return null;
    }
  }

  async getHeroSlides() {
    try {
      const data = await this.request('/cms/hero');
      return (data.slides || []).map((slide) =>
        normalizeMediaFields(slide, ['image_url']),
      );
    } catch {
      return [];
    }
  }

  async getStats() {
    try {
      const data = await this.request('/cms/stats');
      return data.stats || [];
    } catch {
      return [];
    }
  }

  async getFeatures() {
    try {
      const data = await this.request('/cms/features');
      return (data.features || []).map((feature) =>
        normalizeMediaFields(feature, ['image_url']),
      );
    } catch {
      return [];
    }
  }

  async getFaqs(category) {
    try {
      const params = category ? { category } : {};
      const data = await this.request('/cms/faqs', { params });
      return data.faqs || [];
    } catch {
      return [];
    }
  }

  async getTestimonials() {
    try {
      const data = await this.request('/cms/testimonials');
      return (data.testimonials || []).map((testimonial) =>
        normalizeMediaFields(testimonial, ['photo_url']),
      );
    } catch {
      return [];
    }
  }

  async getPartnerLogos(category) {
    try {
      const params = category ? { category } : {};
      const data = await this.request('/cms/partners', { params });
      const partners = (data.partners || []).map(mapPartnerLogo);
      if (partners.length > 0) {
        return partners;
      }

      const supplierData = await this.request('/suppliers');
      return (supplierData.suppliers || []).map(mapSupplierAsPartner);
    } catch {
      return [];
    }
  }

  async getPageContent(pageKey) {
    try {
      const data = await this.request(`/cms/pages/${pageKey}`);
      return normalizeMediaFields(data, ['hero_image_url']);
    } catch {
      return null;
    }
  }

  // ==================== AUTHENTICATION ====================
  
  async register(name, email, password) {
    const result = await this.jsonRpcRequest('/auth/register', { name, email, password });
    if (result.success && result.token) {
      this.setAuthToken(result.token);
    }
    return result;
  }

  async loginEmail(email, password) {
    const result = await this.jsonRpcRequest('/auth/login', { email, password });
    if (result.success && result.token) {
      this.setAuthToken(result.token);
    }
    return result;
  }

  async sendPhoneOtp(phone) {
    return await this.jsonRpcRequest('/auth/phone/send-otp', { phone });
  }

  async verifyPhoneOtp(phone, otp) {
    const result = await this.jsonRpcRequest('/auth/phone/verify', { phone, otp });
    if (result.success && result.token) {
      this.setAuthToken(result.token);
    }
    return result;
  }

  async loginSocial(provider, providerData = {}) {
    const result = await this.jsonRpcRequest('/auth/social', {
      provider,
      idToken: providerData.idToken,
      accessToken: providerData.accessToken,
      tokenSource: providerData.tokenSource,
    });
    if (result.success && result.token) {
      this.setAuthToken(result.token);
    }
    return result;
  }

  async getCurrentUser() {
    try {
      const data = await this.request('/auth/me');
      return data.user || null;
    } catch {
      return null;
    }
  }

  async logout() {
    try {
      await this.jsonRpcRequest('/auth/logout', {});
    } catch {
      // Ignore errors
    }
    this.setAuthToken(null);
  }

  async requestPasswordReset(email) {
    try {
      return await this.jsonRpcRequest('/auth/forgot-password', { email });
    } catch (e) {
      return { success: false, error: e.message || 'Failed to request password reset' };
    }
  }

  async resetPassword(token, newPassword) {
    try {
      return await this.jsonRpcRequest('/auth/reset-password', { token, newPassword });
    } catch (e) {
      return { success: false, error: e.message || 'Failed to reset password' };
    }
  }

  async updateProfile(profileData) {
    return await this.jsonRpcRequest('/user/profile', profileData);
  }

  // ==================== FAVORITES ====================

  async getFavorites() {
    try {
      const data = await this.request('/user/favorites');
      return (data.favorites || []).map(mapCar);
    } catch {
      return [];
    }
  }

  async addFavorite(carId) {
    return await this.jsonRpcRequest(`/user/favorites/${carId}`, {});
  }

  async removeFavorite(carId) {
    return await this.request(`/user/favorites/${carId}`, {
      method: 'DELETE',
      body: {},
    });
  }

  // ==================== REVIEWS ====================

  async getCarReviews(carId) {
    try {
      const data = await this.request(`/cars/${carId}/reviews`);
      return {
        reviews: data.reviews || [],
        averageRating: data.averageRating || 0,
        totalReviews: data.totalReviews || 0,
      };
    } catch {
      return { reviews: [], averageRating: 0, totalReviews: 0 };
    }
  }

  async createReview(carId, reviewData) {
    return await this.jsonRpcRequest(`/cars/${carId}/reviews`, reviewData);
  }

  async markReviewHelpful(reviewId, helpful = true) {
    return await this.jsonRpcRequest(`/reviews/${reviewId}/helpful`, { helpful });
  }

  // ==================== BOOKINGS ====================

  async getBookings() {
    try {
      const data = await this.request('/bookings');
      return data.bookings || [];
    } catch {
      return [];
    }
  }

  async getBooking(bookingId) {
    try {
      const data = await this.request(`/bookings/${bookingId}`);
      return data.booking || null;
    } catch {
      return null;
    }
  }

  async getBookingPreview(previewData) {
    return await this.jsonRpcRequest('/bookings/preview', previewData);
  }

  async createBooking(bookingData) {
    const response = await this.jsonRpcRequest('/bookings', bookingData);

    if (response?.success) {
      this.trackBooking({ carId: bookingData.carId || bookingData.car_id });
    }

    return response;
  }

  async cancelBooking(bookingId, reason) {
    return await this.jsonRpcRequest(`/bookings/${bookingId}/cancel`, { reason });
  }

  // ==================== PAYMENTS ====================

  async createPaymentIntent(bookingId) {
    return await this.jsonRpcRequest('/payments/create-intent', { bookingId });
  }

  async confirmPayment(paymentIntentId) {
    return await this.jsonRpcRequest('/payments/confirm', { paymentIntentId });
  }

  // ==================== LOYALTY PROGRAM ====================

  async getLoyaltyInfo() {
    try {
      return await this.request('/user/loyalty');
    } catch {
      return null;
    }
  }

  async getLoyaltyHistory() {
    try {
      const data = await this.request('/user/loyalty/history');
      return data.history || [];
    } catch {
      return [];
    }
  }

  async redeemLoyaltyPoints(points) {
    return await this.jsonRpcRequest('/user/loyalty/redeem', { points });
  }

  // ==================== PUSH NOTIFICATIONS ====================

  async registerPushToken(token) {
    return await this.jsonRpcRequest('/user/push-token', { token });
  }

  async updatePushSettings(enabled) {
    return await this.request('/user/push-settings', {
      method: 'PUT',
      body: { enabled },
    });
  }

  // ==================== ANALYTICS ====================

  async trackEvent(eventData) {
    try {
      const payload = {
        source: 'web',
        sessionId: this.getAnalyticsSessionId(),
        ...eventData,
      };

      if (!payload.pageUrl) {
        payload.pageUrl = this.getCurrentPageUrl();
      }

      if (!payload.referrer && typeof document !== 'undefined') {
        payload.referrer = document.referrer;
      }

      await this.jsonRpcRequest('/analytics/track', payload);
    } catch {
      // Silent fail - analytics should not break the app
    }
  }

  trackPageView(pageUrl) {
    this.trackEvent({
      eventType: 'page_view',
      pageUrl,
    });
  }

  trackCarView(carId, pageUrl) {
    this.trackEvent({
      eventType: 'car_view',
      carId,
      pageUrl,
    });
  }

  trackSearch(query, filters, pageUrl) {
    this.trackEvent({
      eventType: 'search',
      searchQuery: query,
      searchFilters: filters,
      pageUrl,
    });
  }

  trackInquiry({ carId, pageUrl } = {}) {
    this.trackEvent({
      eventType: 'inquiry',
      carId,
      pageUrl,
    });
  }

  trackBooking({ carId, pageUrl } = {}) {
    this.trackEvent({
      eventType: 'booking',
      carId,
      pageUrl,
    });
  }

  // ==================== ADMIN DASHBOARD ====================

  async getAdminDashboard() {
    try {
      return await this.request('/admin/dashboard');
    } catch {
      return null;
    }
  }
}

const odooApi = new OdooApiService();
export default odooApi;
export {
  OdooApiService,
  ODOO_API_BASE,
  DEFAULT_CONTACT_PHONE,
  DEFAULT_CONTACT_PHONE_DISPLAY,
  BRAND_CODES,
  FALLBACK_IMAGES,
  buildPhoneHref,
  buildWhatsAppUrl,
  getDisplayPhoneNumber,
  sanitizePhoneNumber,
  mapCar,
  mapBrand,
  mapCategory,
  mapCity,
  mapSupplier,
};
